import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Page, ReservationDetailsCard, Spinner } from '../../../components'
import { IAvailability, IActivePass, IStop, ITicketingType, IUser, IPaymentActions, IPaymentState, AuthenticatedRoutable } from '../../../models'
import { LiteralsService, UserService } from '../../../services'
import { confirmNewReservationAction, fetchActivePassesAction, setStripeAccountId, setUserInfoAction } from '../../../store/actions'
import { SelectActivePassModal } from '../../../containers/NewReservation/SelectActivePassModal'
import { appsTemporalPassesSelected } from '../../../helpers/app'
import { IStoreState } from '../../../store/states'
import theme from '../../../theme'
import StyledArticle from '../styles'
import { ReservationInstructions } from '../../../containers/NewReservation/Instructions'
import { appsStyleSelected } from '../../../helpers/app'
import { AddPaymentModal } from '../../../containers/NewReservation/AddPaymentModal'
import { ConfirmPaymentModal } from '../../../containers/NewReservation/ConfirmPaymentModal'
import { ElementsConsumer } from '@stripe/react-stripe-js'
import { urls } from '../../../routing'
import { Application } from '../../../config'
import { AnyAction } from 'redux'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  pickedAvailability: IAvailability | null
  isConfirmingReservation: boolean
  pickedSeats: number
  pickedPrmSeats: number
  pickedService: number | null
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
  userInfo: IUser,
  paymentState: IPaymentState
  returnPickedAvailability: IAvailability
  privacyPolicyUrl: string
  amountPaid: number
  returnAmountPaid: number
  isEdit: boolean
  activePasses: IActivePass[]
}

interface IState {
  displayConfirmPaymentModal: boolean
  displayPaymentMethodModal: boolean
  displaySelectPassModal: boolean
}

const { isIframe } = Application

class ConfirmationStep extends AuthenticatedRoutable<IProps, IState> {
  
  private get displaySaveButton(): ReactNode {
    const { isConfirmingReservation } = this.props
    if (isConfirmingReservation) return <Spinner />

    const buttons = this.getButtonInfo().map((button, i) => (
      <Button 
        key={ i }
        title={ button.value }
        onPress={ () => this.onValidatedButtonPress(button.actionKey) }
        isDisabled={ this.props.isConfirmingReservation}
        styles={{
          boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
        }}
        filled
        upper
      />
    ))

    return (
      <div className='buttons-container'>
        { buttons }
      </div>
    )
  }

  private get instructions(): ReactNode {
    return (
      <ReservationInstructions
        textTitle= {LiteralsService.get('tutorialCheckDetailsTitle', true)}
        textContent= {LiteralsService.get('tutorialCheckDetailsContent', true)}
        backgroundColor= {theme.colors.greyLight}
      />
    )
  }

  private getButtonInfo(): any[] {
    const { pickedAvailability, returnPickedAvailability, amountPaid, returnAmountPaid, isEdit } = this.props

    let titles = [
      {
        actionKey: IPaymentActions.BOOK,
        value: LiteralsService.get('book', true)
      }
    ]
    let price = pickedAvailability?.price ?? 0
    if (amountPaid && amountPaid > 0) price -= amountPaid

    if (returnPickedAvailability) {
      price = Number(price) + returnPickedAvailability.price
      if (returnAmountPaid && returnAmountPaid > 0) price -= returnAmountPaid
    }
    
    if (pickedAvailability && pickedAvailability.ticketingType === ITicketingType.None)
      titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('book', true) } ]
    else if (pickedAvailability && pickedAvailability?.ticketingType === ITicketingType.Integrated)
      titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('book', true) } ]
    else if (pickedAvailability && price == 0) {
      if (isEdit) titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('edit', true) } ]
      else titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('book', true) } ]
    } else if (pickedAvailability && price < 0)
      titles = [ { actionKey: IPaymentActions.REFUND, value: `${LiteralsService.get('refund', true)} ${price.toString().slice(1)}€` } ]
    else if (pickedAvailability && pickedAvailability?.ticketingType === ITicketingType.Onboard)
      titles = [ { actionKey: IPaymentActions.PAY_IN_BUS, value: `${LiteralsService.get('payInBus', true)} ${price}€` } ]
    else if (pickedAvailability && pickedAvailability.ticketingType === ITicketingType.Online)
      titles = [ { actionKey: IPaymentActions.PAY, value: `${LiteralsService.get('pay', true)} ${price}€` }]
    else if (pickedAvailability && pickedAvailability.ticketingType === ITicketingType.Hybrid)
      titles = [ { actionKey: IPaymentActions.PAY_IN_BUS, value: `${LiteralsService.get('payInBus', true)} ${price}€` }, { actionKey: IPaymentActions.PAY, value: `${LiteralsService.get('pay', true)} ${price}€` } ]
    else if (pickedAvailability && pickedAvailability.ticketingType === ITicketingType.Periodically)
      titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('book', true) } ]
    else
      titles = [ { actionKey: IPaymentActions.BOOK, value: LiteralsService.get('book', true) } ]

    if (this.canUsePass(price)) {
      titles = [ ...titles, { actionKey: IPaymentActions.USE_PASS, value: LiteralsService.get('usePass', true) } ]
    }

    return titles
  }

  // A pass only replaces a real payment: free trips, refunds and non-ticketed
  // services have nothing to spend it on.
  private canUsePass(price: number): boolean {
    const { pickedAvailability } = this.props
    if (!appsTemporalPassesSelected?.enabled) return false
    if (!pickedAvailability) return false
    if (!pickedAvailability.ticketingType || pickedAvailability.ticketingType === ITicketingType.None) return false
    if (price <= 0) return false

    return !!pickedAvailability.temporalPassIds?.length
  }

  private get matchingActivePasses(): IActivePass[] {
    const { activePasses, pickedAvailability } = this.props
    const allowedIds = pickedAvailability?.temporalPassIds

    if (!activePasses?.length || !allowedIds?.length) return []

    return activePasses.filter(pass => allowedIds.includes(pass.temporalPass.id))
  }

  constructor(props: IProps) {
    super(props)

    this.state = {
      displayConfirmPaymentModal: false,
      displayPaymentMethodModal: false,
      displaySelectPassModal: false
    }
  }

  componentDidMount(): void {
    const { userInfo, dispatch } = this.props
    if (!userInfo.id) super.componentDidMount()
    else if (userInfo.stripeIds && !isIframe) this.onUpdateUserInfo()
    if (appsTemporalPassesSelected?.enabled) dispatch(fetchActivePassesAction())
  }

  componentDidUpdate(prevProps: IProps): void {
    // The confirmation finished (either way): the pass modal must not stay open behind it.
    if (prevProps.isConfirmingReservation && !this.props.isConfirmingReservation) {
      this.setState({ displaySelectPassModal: false })
    }
  }

  public render(): ReactNode {

    const {
      pickedAvailability,
      pickedSeats,
      pickedPrmSeats,
      pickedService,
      pickedOriginBusStop,
      pickedDestinationBusStop,
      paymentState,
      returnPickedAvailability,
      dispatch,
      history,
      privacyPolicyUrl
    } = this.props
    const {
      displayConfirmPaymentModal,
      displayPaymentMethodModal
    } = this.state

    if (!pickedAvailability) return null

    let price = pickedAvailability.price
    if (returnPickedAvailability) price = price + returnPickedAvailability.price

    return (
      <Page className={`authenticated ${appsStyleSelected?.style}`}>
        <StyledArticle className={`${appsStyleSelected?.style}`}>
          <section>
            <section>
              <header style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, margin: 0, paddingTop: 8 }}>
                { !isIframe &&
                  <h3>
                    { LiteralsService.get('routeDetails').toUpperCase() }
                  </h3>
                }
                { this.instructions }
              </header>
            </section>
            <section style={{ marginTop: 16 }}>
              <ReservationDetailsCard
                availability={ pickedAvailability }
                returnAvailability={ returnPickedAvailability }
                seats={ pickedSeats }
                prmSeats={ pickedPrmSeats }
                busLocation={ null }
                pickedOriginBusStop={ pickedOriginBusStop }
                pickedDestinationBusStop={ pickedDestinationBusStop }
                extraInfoMessage
                aprox
                hideMapOnMobile={ !!returnPickedAvailability }
                showRecenterButton={ false }
              />
            </section>
          </section>
          <footer>
            <section className='buttons-wrapper'>
              { this.displaySaveButton }
              <Button
                title={ LiteralsService.get('previous') }
                onPress={ () => history.push(urls.newReservationDateStep, {
                  userData: history.location.state?.userData,
                  townId: history.location.state?.townId,
                }) }
                isDisabled={ this.props.isConfirmingReservation }
                styles={{
                  backgroundColor: theme.colors.white,
                  borderColor: theme.colors.primary,
                  borderWidth: 2,
                  boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                }}
                upper
              />
            </section>
          </footer>
        </StyledArticle>
        <ElementsConsumer>
          {({stripe, elements}) => {
            return <AddPaymentModal
              stripe={ stripe! }
              elements={ elements! }
              visible={ displayPaymentMethodModal }
              showAddOption={ true }
              addOptionText={ LiteralsService.get('addPaymentDescription', true) }
              amount={ price }
              pickedService={ pickedService }
              privacyPolicyUrl={ privacyPolicyUrl }
              dispatch={ dispatch }
              updateUserInfo={ this.onUpdateUserInfo }
              close={ this.toggleDisplayPaymentMethodModal }
            />
          }
          }
        </ElementsConsumer>
        {
          this.state.displaySelectPassModal &&
          <SelectActivePassModal
            passes={ this.matchingActivePasses }
            seats={ this.props.pickedSeats + this.props.pickedPrmSeats }
            isConfirming={ this.props.isConfirmingReservation }
            onConfirm={ this.confirmWithPass }
            onClose={ this.closeSelectPassModal }
          />
        }
        <ConfirmPaymentModal
          visible={ displayConfirmPaymentModal }
          paymentState={ paymentState }
          isRefund={ pickedAvailability && pickedAvailability.price < 0 }
          close={ this.toggleConfirmPaymentModal }
          confirmPayment={ this.confirmPayment }
        />
      </Page>
    )
  }

  private onValidatedButtonPress = (action?: string) => {
    const { userInfo } = this.props
    switch(action) {
      case IPaymentActions.REFUND:
      case IPaymentActions.PAY:
        if (userInfo.stripeIds?.length! > 0 && !isIframe) {
          this.toggleConfirmPaymentModal()
        } else {
          this.toggleDisplayPaymentMethodModal()
        }
        break;
      case IPaymentActions.PAY_IN_BUS:
        this.props.dispatch(confirmNewReservationAction(IPaymentActions.PAY_IN_BUS))
        break;
      case IPaymentActions.USE_PASS:
        this.setState({ displaySelectPassModal: true })
        break;
      default:
      case IPaymentActions.BOOK:
        this.props.dispatch(confirmNewReservationAction())
        break;
    }
  }

  private onUpdateUserInfo = async() => {
    const user = await UserService.getInfo()
    this.props.dispatch(setUserInfoAction(user))
    this.updateAccountId()
  }

  private toggleDisplayPaymentMethodModal = () => {
    const { displayPaymentMethodModal } = this.state
    this.setState({
      displayPaymentMethodModal: !displayPaymentMethodModal
    })
  }

  private toggleConfirmPaymentModal = () => {
    const { displayConfirmPaymentModal } = this.state
    this.setState({
      displayConfirmPaymentModal: !displayConfirmPaymentModal
    })
  }

  private updateAccountId = async () => {
    const { dispatch, pickedService } = this.props
    await UserService.getAccountId(pickedService!).then((res: any) => {
      dispatch(setStripeAccountId(res.accountId))
    })
  }

  private closeSelectPassModal = () => {
    if (this.props.isConfirmingReservation) return
    this.setState({ displaySelectPassModal: false })
  }

  private confirmWithPass = (activePassId: number) => {
    this.props.dispatch(confirmNewReservationAction(IPaymentActions.USE_PASS, activePassId))
  }

  private confirmPayment = async () => {
    const { dispatch } = this.props
    dispatch(confirmNewReservationAction(IPaymentActions.PAY))
  }
  
}

const mapStateToProps = ({ newReservation, userInfo, configuration, wallet }: IStoreState) => ({
  activePasses: wallet.activePasses,
  pickedAvailability: newReservation.pickedAvailability,
  isConfirmingReservation: newReservation.isConfirmingReservation,
  pickedSeats: newReservation.pickedSeats,
  pickedPrmSeats: newReservation.pickedPrmSeats,
  pickedService: newReservation.pickedAvailability?.serviceId,
  pickedOriginBusStop: newReservation.pickedOriginBusStop,
  pickedDestinationBusStop: newReservation.pickedDestinationBusStop,
  userInfo: userInfo,
  paymentState: newReservation.paymentState,
  returnPickedAvailability: newReservation.returnPickedAvailability,
  privacyPolicyUrl: configuration.clientInfo?.privacyPolicyUrl || '',
  amountPaid: newReservation.routeOptionsAndFailures?.amountPaid,
  returnAmountPaid: newReservation.returnRouteOptionsAndFailures?.amountPaid,
  isEdit: newReservation.isEdit
})

export default withRouter(connectRedux(mapStateToProps, ConfirmationStep as any))
